import { useSyncExternalStore } from "react";

/**
 * Is the viewport wide enough to run a guided flow?
 *
 * The guides have always been desktop-only in their CSS — the rail and both
 * checklist launchers are `hidden sm:block` — but nothing stopped them
 * STARTING on a phone. Neither gate checked the viewport, and the welcome
 * modals are fully responsive, so a phone visitor got the full-screen
 * chooser, picked an asset class, had a dashboard template applied, and then
 * met every piece of guidance at `display: none`. No rail, no checklist, no
 * way back in, and a stored state saying the run was live.
 *
 * Gating the gates rather than widening the CSS is the honest fix for now:
 * the flows point at controls (the highlight menu, the popout icon, the
 * builder's two cards) that a phone layout either moves or does not show, so
 * there is no version of them that merely needs to be un-hidden.
 *
 * 640px is Tailwind's `sm`, matching the breakpoint the chrome already uses —
 * one number, one place, no chance of the gate and the CSS disagreeing.
 */
export const GUIDE_MIN_WIDTH = 640;

const QUERY = `(min-width: ${GUIDE_MIN_WIDTH}px)`;

/**
 * SSR/JSDOM-safe: no matchMedia means no opinion, and "wide enough" is the
 * answer that preserves existing behaviour on anything that cannot tell us.
 */
function getSnapshot(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia(QUERY).matches;
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const list = window.matchMedia(QUERY);
  // addEventListener rather than addListener: the latter is deprecated and
  // gone from newer WebKit, which is most of the audience this gate is for.
  list.addEventListener("change", onChange);
  return () => list.removeEventListener("change", onChange);
}

/**
 * Reactive so rotating a tablet, or dragging a desktop window narrow, takes
 * the guide off screen rather than leaving a rail pinned over a layout that
 * has stopped matching it.
 */
export function useGuideViewport(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}
